import { useCallback, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { dashboardApi, withTimeout } from '../services/dashboardApi';
import { auditApi } from '../services/auditApi';
import {
  AuditDashboard,
  DashboardWindowName,
  GaragesActivity,
  GaragesNeedsAction,
  OrdersDashboard,
  RefundsDashboard,
  ReviewsDashboard,
  UsersActivity,
} from '../types/audit';
import { useDashboardBlock, DashboardBlock } from '../components/dashboard/useDashboardBlock';
import NeedsActionBoard, { sourceNeeded } from '../components/dashboard/NeedsActionBoard';
import ActivityCounters from '../components/dashboard/ActivityCounters';
import MyActivityCard from '../components/dashboard/MyActivityCard';
import BusinessStatusStrip from '../components/dashboard/BusinessStatusStrip';
import { formatGstTime } from '../components/audit/auditFormat';

/**
 * Spec 069 Flow A — the work-queue dashboard. Seven aggregates load in parallel, each with its own
 * 3 s timeout and Retry; tiles show only when the viewer holds the screen that owns the list.
 */
export default function DashboardV2Page() {
  const { token, user, hasScreen } = useAuth();
  const features = useFeatureFlags();
  const [win, setWin] = useState<DashboardWindowName>('TODAY');
  const [refreshedAt, setRefreshedAt] = useState(() => new Date());
  const [refreshNo, setRefreshNo] = useState(0);
  const key = `${refreshNo}`;
  const windowKey = `${refreshNo}:${win}`;

  const t = token ?? '';
  const on = (cond: boolean) => !!token && cond;

  const garagesNeeds = useDashboardBlock<GaragesNeedsAction>(
    on(sourceNeeded('garages', hasScreen)) ? (s) => dashboardApi.needsAction(t, s) : null, key);
  const refunds = useDashboardBlock<RefundsDashboard>(
    on(sourceNeeded('refunds', hasScreen)) ? (s) => dashboardApi.refundsDashboard(t, s) : null, key);
  const orders = useDashboardBlock<OrdersDashboard>(
    on(true) ? (s) => dashboardApi.ordersDashboard(win, t, s) : null, windowKey);
  const reviews = useDashboardBlock<ReviewsDashboard>(
    on(true) ? (s) => dashboardApi.reviewsDashboard(win, t, s) : null, windowKey);
  const garagesActivity = useDashboardBlock<GaragesActivity>(
    on(true) ? (s) => dashboardApi.activity(win, t, s) : null, windowKey);
  const users = useDashboardBlock<UsersActivity>(
    on(true) ? (s) => dashboardApi.usersActivity(win, t, s) : null, windowKey);
  const audit = useDashboardBlock<AuditDashboard>(
    on(true) ? (s) => withTimeout((inner) => auditApi.dashboard(win, t, inner), s) : null, windowKey);

  const refresh = useCallback(() => {
    setRefreshNo((n) => n + 1);
    setRefreshedAt(new Date());
  }, []);

  const changeWindow = (w: DashboardWindowName) => {
    setWin(w);
    setRefreshedAt(new Date());
  };

  const now = refreshedAt;
  const boardBlocks = {
    garages: garagesNeeds as DashboardBlock<unknown>,
    orders: orders as DashboardBlock<unknown>,
    refunds: refunds as DashboardBlock<unknown>,
    reviews: reviews as DashboardBlock<unknown>,
  };

  return (
    <div className="dash-v2">
      <div className="dash-v2__head">
        <h2 className="dash-v2__title">Dashboard</h2>
        <div className="dash-v2__updated">
          <span>updated {formatGstTime(refreshedAt)}</span>
          <span aria-hidden="true"> · </span>
          <button type="button" className="dash-link-btn" onClick={refresh}>refresh</button>
        </div>
      </div>

      <MyActivityCard
        block={audit}
        window={win}
        subjectId={user?.id}
        canOpenAudit={features.auditScreen && hasScreen('audit')}
      />

      {/* Spec 070 — its own switch (D1); the strip and its three requests exist only when it is on. */}
      {features.businessStatus && token && <BusinessStatusStrip token={token} reloadKey={key} />}

      <NeedsActionBoard blocks={boardBlocks} hasScreen={hasScreen} now={now} />

      <ActivityCounters
        window={win}
        onWindowChange={changeWindow}
        garages={garagesActivity}
        users={users}
        orders={orders}
        reviews={reviews}
        audit={audit}
      />
    </div>
  );
}
